import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Type } from '@angular/core';

import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { BaseRestService } from '../base-rest.service';
import { UserService } from '../user.service';
import { SubjectType } from '../../models/subject-type';
import { plural } from 'pluralize';
import { map, take } from 'rxjs/operators';
import { User } from '../../models/user';
import { LookupTypeService } from '../lookup-type.service';
import {
  enumGroupReportReturnType,
  IReportList,
  Report,
  ReportConfig,
  ReportOptions,
} from '../../models/report';
import { DataField } from '../../models/data-field';
import { Guid } from '../../helpers/guid';
import { IDataService } from '../data.service';

export interface IReportService extends IDataService {
  subjectType: SubjectType;
  getAllData(
    type?: string,
    urlParams?: string,
    forceRefresh?: boolean
  ): Observable<Report[]>;
  getFilters(dataType: string): Observable<any>;
  getFilterName(dataType: string): Observable<string>;
  getReportData(report: Report, options: ReportOptions): void;
  save(entity: Report, successMessage?: string);
  reset();
}
export class BaseReportService
  extends BaseRestService<Report, IReportList>
   implements IReportService
{
  subjectType: SubjectType; // keep track of the current subject type
  currentOptions: ReportOptions; // keep track of currently applied options

  currentUser: User;

  itemChanged$: Subscription;

  constructor(
    protected type: Type<any>,
    protected http: HttpClient,
    protected baseResourceUrl: string,
    protected name: string,
    protected router: Router,
    protected lookupTypeService: LookupTypeService,
    protected userService: UserService
  ) {
    super(type, http, baseResourceUrl, name, router);
  }

  getAllData(
    type?: string,
    urlParams?: string,
    forceRefresh?: boolean
  ): Observable<Report[]> {
    return super.getAllData(type, urlParams, forceRefresh).pipe(
      map((reports) => {
        reports.forEach((i) => (i.canDelete = true));
        const result = reports.filter((i) => i.id > 0);
        return this.updateCache(result);
      })
    );
  }

  getReportData(report: Report, options: ReportOptions): void {}

  protected updateCache(reports: Report[]): Report[] {
    this.replaceCache(
      reports && reports.length > 0
        ? [...reports.sort((a, b) => a.name.localeCompare(b.name))]
        : []
    );
    return this.getCache();
  }

  hydrate(item: Report): Observable<Report> {
    item.config = JSON.parse(item.metadata);

    const hydrate$ = [];
    if (
      item.createdById &&
      (!item.createdBy || item.createdBy.id !== item.createdById)
    ) {
      hydrate$.push(
        this.userService.getById(item.createdById).pipe(
          take(1),
          map((user) => {
            item.createdBy = new User(user);
            return item;
          })
        )
      );
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(
        take(1),
        map((_) => {
          item.createdByName = item.createdBy
            ? new User(item.createdBy).fullName
            : '';
          return item;
        })
      );
    } else {
      item.createdByName = item.createdBy
        ? new User(item.createdBy).fullName
        : '';
      return of(item);
    }
  }

  save(entity: Report, successMessage?: string) {
    entity.config.options = this.currentOptions;
    entity.metadata = JSON.stringify(entity.config);
    entity.createdById = this.userService.getCurrentUserId();
    entity.createdBy = null;
    super.save(entity, successMessage);
  }

  buildUrl(report: Report, options: ReportOptions) {
    this.currentOptions = options;
    // as a hack to allow optional parameters I pass an X for empty values
    const values = `${report.config.groupBy}/${report.config.valueId || 'X'}/${
      report.config.aggregate || 'count'
    }`;
    const reportType = `${options.reportType || 'X'}`;
    const dateFilters = `${options.dateType || 'X'}/${
      options.dateFrom || 'X'
    }/${options.dateTo || 'X'}`;
    const selectFilters =
      `${report.config.organisationFilterId || 'X'}/${
        options.organisations || 'X'
      }` +
      `/${report.config.filterId || 'X'}/${options.filters || 'X'}` +
      `/${report.config.subFilterId || 'X'}/${options.subFilters || 'X'}` +
      `/${report.config.userFilterId || 'X'}/${options.users || 'X'}` +
      `/${report.config.includeRecordsNotLinkedToSubject}`;

    return (
      this.resourceUrl +
      `/groupReport/${values}/${
        options.state || 'X'
      }/${reportType}/${dateFilters}/${options.includeArchived}` +
      `/${
        report.config.returnType || enumGroupReportReturnType.Subject
      }/${selectFilters}`
    );
  }

  createReport(
    displayName: string,
    description: string,
    groupBy: DataField,
    organisation?: DataField,
    filter?: DataField,
    subFilter?: DataField,
    user?: DataField,
    value?: any,
    subjectTypeId?: number,
    processTypeId?: number,
    includeRecordsNotLinkedToSubject?: boolean //modify report config to hold whatever value is back in
  ) {
    const reportConfig = new ReportConfig({ groupBy: groupBy.id });
    if (organisation) {
      reportConfig.organisationFilterId = organisation.id;
      reportConfig.organisationFilterLabel = plural(organisation.label);
    }
    if (filter) {
      reportConfig.filterId = filter.id;
      reportConfig.filterDataType = filter.dataType.toLowerCase();
      reportConfig.filterLabel = plural(filter.label).replace(' Bies', ' By');
    }
    if (subFilter) {
      reportConfig.subFilterId = subFilter.id;
      reportConfig.subFilterDataType = subFilter.dataType.toLowerCase();
      reportConfig.subFilterLabel = plural(subFilter.label).replace(
        ' Bies',
        ' By'
      );
    }
    if (user) {
      reportConfig.userFilterId = user.id;
      reportConfig.userFilterLabel = plural(user.label);
    }
    if (value) {
      reportConfig.valueId = value.id;
      reportConfig.valueLabel = value.label;
      if (value.type) {
        reportConfig.aggregate = value.type.toLowerCase();
      }
    }
    if (subjectTypeId) {
      reportConfig.subjectTypeId = subjectTypeId;
    }
    if (processTypeId) {
      reportConfig.processTypeId = processTypeId;
    }

    if (includeRecordsNotLinkedToSubject) {
      reportConfig.includeRecordsNotLinkedToSubject = true;
    }

    return new Report({
      uid: Guid.newGuid(),
      name: displayName,
      description,
      reportType: 'Group Report',
      config: reportConfig,
      canDelete: false,
    });
  }

  getFilters(dataType: string): Observable<any> {
    return of(null);
  }

  getFilterName(dataType: string): Observable<string> {
    switch (dataType.toLowerCase()) {
      case 'category':
        return of('Categories');
      case 'status':
        return of('Statuses');
      case 'user':
        return of('Users');
      case 'timetype':
        return of('Time Types');
      default:
        if (/^\d+$/.test(dataType)) {
          return this.lookupTypeService.getById(Number(dataType)).pipe(
            map((lookupType) => {
              return lookupType ? plural(lookupType.description) : 'Filters';
            })
          );
        } else {
          return of('Filters');
        }
    }
  }

  reset() {
    this.clearCache();
    this.getAll();
  }

  isUnique(
    val: string,
    id: number | string,
    propertyName: string
  ): Observable<boolean> {
    if (val) {
      const item = this.cache.find(
        (i) =>
          i[propertyName] &&
          i.uid !== id &&
          i[propertyName].toString().toLowerCase().replace(/\s/g, '') ===
            val.toString().toLowerCase().replace(/\s/g, '')
      );
      return of(item ? false : true);
    } else {
      return of(true);
    }
  }
}
